import React, { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';

interface HydrogenBubblesProps {
  position?: [number, number, number];
  /** Whether bubbles should be emitted right now. */
  active?: boolean;
  /** Vertical column height in metres over which bubbles rise. */
  heightRange?: number;
  /** Spread of bubble emission in metres (XZ radius). */
  spreadRadius?: number;
  /** Number of bubble particles in the pool. */
  bubbleCount?: number;
  /** Optional rate multiplier for bubble speed. */
  speed?: number;
}

/**
 * Hydrogen bubble particle system — small spherical bubbles that rise
 * upward through a column of liquid. Used inside the gas-generator
 * boiling tube (bubbles forming on the zinc) and inside the water
 * safety bottle (bubbles entering at the dip tube and rising to the
 * surface), where they act as a visible H₂ flow indicator.
 */
export function HydrogenBubbles({
  position = [0, 0, 0],
  active = false,
  heightRange = 0.16,
  spreadRadius = 0.038,
  bubbleCount = 40,
  speed = 1.0,
}: HydrogenBubblesProps) {
  const ref = useRef<THREE.InstancedMesh>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);

  const seeds = useMemo(() => Array.from({ length: bubbleCount }, () => ({
    speed: 0.22 + Math.random() * 0.32,
    phase: Math.random(),
    rx: (Math.random() - 0.5) * spreadRadius * 1.8,
    rz: (Math.random() - 0.5) * spreadRadius * 1.8,
    sway: 0.005 + Math.random() * 0.012,
    swayPhase: Math.random() * Math.PI * 2,
    size: 0.0028 + Math.random() * 0.0024,
  })), [bubbleCount, spreadRadius]);

  useFrame((s) => {
    if (!ref.current) return;
    if (!active) {
      for (let i = 0; i < bubbleCount; i++) {
        dummy.position.set(0, -999, 0);
        dummy.scale.setScalar(0);
        dummy.updateMatrix();
        ref.current.setMatrixAt(i, dummy.matrix);
      }
      ref.current.instanceMatrix.needsUpdate = true;
      return;
    }
    const t = s.clock.elapsedTime;
    for (let i = 0; i < bubbleCount; i++) {
      const b = seeds[i];
      const cycle = (t * b.speed * speed + b.phase) % 1;
      const y = cycle * heightRange;
      const x = b.rx + Math.sin(t * 2.2 + b.swayPhase) * b.sway;
      const z = b.rz + Math.cos(t * 2.0 + b.swayPhase) * b.sway;
      // Bubbles grow slightly as they rise (pressure drop)
      const sizeScale = 1 + cycle * 0.25;
      dummy.position.set(x, y, z);
      dummy.scale.setScalar(b.size * sizeScale);
      dummy.updateMatrix();
      ref.current.setMatrixAt(i, dummy.matrix);
    }
    ref.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <group position={position}>
      <instancedMesh ref={ref} args={[undefined, undefined, bubbleCount]}>
        <sphereGeometry args={[1, 10, 10]} />
        <meshPhysicalMaterial
          color="#e9f6ff"
          transparent
          opacity={0.75}
          transmission={0.85}
          roughness={0.05}
          ior={1.0}
          depthWrite={false}
        />
      </instancedMesh>
    </group>
  );
}

export default HydrogenBubbles;
